import { inject, Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, Observable } from 'rxjs';

import { CurrentUserSelectors } from '@ninety/mobile/platform/state';

import { DiscussionListItem } from '../models/discussion.models';

/*
 * TODO:
 * Logic here should be moved to the backend and the properties we need
 * included in the response. The business logic was copied from the web frontend
 */

/** Provides context based on the logged-in user and state of a given Discussion */
@Injectable()
export class DiscussionContextService {
  private store = inject(Store);

  currentUser$ = this.store.select(CurrentUserSelectors.selectUser);
  currentUserId$ = this.currentUser$.pipe(
    filter(u => !!u?._id),
    map(u => u?._id ?? '')
  );

  /**
   * Indicates whether the discussion form is in progress for the logged-in user.
   * A form is in progress when the user has started the form but not yet completed it.
   */
  userStartedForm(c: DiscussionListItem) {
    return this.currentUserId$.pipe(
      map(currentUserId => {
        if (currentUserId === c.managerUserId && c.hasManagerStarted && !c.isManagerDone) return true;

        return !!(currentUserId === c.manageeUserId && c.hasManageeStarted && !c.isManageeDone);
      })
    );
  }

  /** Indicates if the logged-in user has completed their form. */
  userCompletedForm(discussion: DiscussionListItem) {
    return this.currentUserId$.pipe(
      map(currentUserId => {
        if (currentUserId === discussion.managerUserId && discussion.isManagerDone) return true;
        return !!(currentUserId === discussion.manageeUserId && discussion.isManageeDone);
      })
    );
  }

  /** Indicates if the user can edit the discussion form. */
  canEditForm(c: DiscussionListItem) {
    return this.currentUserId$.pipe(
      map(currentUserId => {
        const userIsDiscussionManager = currentUserId === c.managerUserId;
        const userIsDiscussionManagee =
          currentUserId === c.manageeUserId && (c.canEvaluateManager || c.canEvaluateSelf);
        // User is not the discussion manager or managee
        if (!userIsDiscussionManager && !userIsDiscussionManagee) return false;
        // Both forms already completed
        if (c.isManagerDone && c.isManageeDone) return false;
        // discussion is marked as completed
        if (c.isCompleted) return false;
        // discussion meeting is complete
        if (c.isMeetingCompleted) return false;
        if (userIsDiscussionManager) return !c.isManagerDone;
        if (userIsDiscussionManagee) return !c.isManageeDone;
        return false;
      })
    );
  }

  userIsDiscussionManager(discussion: DiscussionListItem) {
    return this.currentUserId$.pipe(
      map(currentUserId => {
        return currentUserId === discussion.managerUserId;
      })
    );
  }

  userIsDiscussionManagee(discussion: DiscussionListItem) {
    return this.currentUserId$.pipe(
      map(currentUserId => {
        return currentUserId === discussion.manageeUserId;
      })
    );
  }

  userIsDiscussionParticipant(discussion: DiscussionListItem): Observable<boolean> {
    return combineLatest([this.userIsDiscussionManager(discussion), this.userIsDiscussionManagee(discussion)]).pipe(
      map(([manager, managee]) => manager || managee)
    );
  }

  /**
   * Indicates if the logged-in user is allowed to view the Manager form on the detail view for a given discussion.
   */
  canViewManagerForm(discussion: DiscussionListItem) {
    return this.currentUser$.pipe(
      concatLatestFrom(() => [this.userIsDiscussionManager(discussion), this.userIsDiscussionParticipant(discussion)]),
      map(([currentUser, isManager, isParticipant]) => {
        const { isMeetingCompleted, isManageeDone, isManagerDone, revealAnswersPriorToMeeting } = discussion;
        if (currentUser?.isAdminOrOwner && !isParticipant) return true;
        if (!isParticipant) return false;
        if (isManager && isManagerDone) return true;
        if (revealAnswersPriorToMeeting && isManagerDone && isManageeDone) return true;
        return isMeetingCompleted;
      })
    );
  }

  /**
   * Indicates if the logged-in user is allowed to view the Managee form on the detail view for a given discussion.
   */
  canViewManageeForm(discussion: DiscussionListItem) {
    return this.currentUser$.pipe(
      concatLatestFrom(() => [this.userIsDiscussionManagee(discussion), this.userIsDiscussionParticipant(discussion)]),
      map(([currentUser, isManagee, isParticipant]) => {
        const { isMeetingCompleted, isManageeDone, isManagerDone, revealAnswersPriorToMeeting } = discussion;
        if (currentUser?.isAdminOrOwner && !isParticipant) return true;
        if (!isParticipant) return false;
        if (isManagee && isManageeDone) return true;
        if (revealAnswersPriorToMeeting && isManagerDone && isManageeDone) return true;
        return isMeetingCompleted;
      })
    );
  }
}
